import { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAnonymous: boolean;
  signOut: () => Promise<void>;
  signInAnonymously: () => Promise<void>;
  convertAnonymousToRegistered: (
    email: string,
    password: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is anonymous (has auth but not in profiles table)
  const isAnonymous = Boolean(user && user.is_anonymous);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // First, try to get existing session
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (mounted) {
          if (session) {
            // User has existing session (authenticated or anonymous)
            setSession(session);
            setUser(session.user);

            // Enhanced logging for quote visibility debugging
            logger.debug(
              '🔐 Auth Session Found - Customer Quote Debug',
              {
                userId: session.user.id,
                email: session.user.email,
                isAnonymous: session.user.is_anonymous,
                hasUserId: !!session.user.id,
                hasEmail: !!session.user.email,
                userMetadata: session.user.user_metadata,
              },
              'Auth',
            );
            
            // Additional console log for customer quote debugging
            console.log('[customer-quotes] 🔐 User authenticated:', {
              id: session.user.id,
              email: session.user.email,
              canAccessQuotes: !!(session.user.id && session.user.email),
            });
          } else {
            // No session exists - don't auto-create anonymous session
            logger.debug('No session found, user needs to sign in', null, 'Auth');
          }
        }
      } catch (error) {
        logger.error('Error initializing auth', error, 'Auth');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    const signInAnonymously = async () => {
      try {
        const { data, error } = await supabase.auth.signInAnonymously();

        if (error) {
          // If anonymous sign-ins are disabled, just continue without auth
          if (error.message?.includes('Anonymous sign-ins are disabled')) {
            logger.warn('Anonymous sign-ins are disabled, continuing without auth', null, 'Auth');
            return;
          }
          // If captcha verification fails, continue without auth
          if (error.message?.includes('captcha') || error.message?.includes('verification')) {
            logger.warn('Captcha verification not supported for anonymous auth, continuing without auth', null, 'Auth');
            return;
          }
          logger.error('Error signing in anonymously', error, 'Auth');
          return;
        }

        if (mounted && data.session) {
          setSession(data.session);
          setUser(data.user);
          logger.debug('Anonymous user signed in', { userId: data.user.id }, 'Auth');
        }
      } catch (error) {
        logger.error('Error in anonymous sign in', error, 'Auth');
      }
    };

    initializeAuth();

    try {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);

        }
      });

      return () => {
        mounted = false;
        subscription.unsubscribe();
      };
    } catch (error) {
      logger.error('Error setting up auth state change listener', error, 'Auth');
      setLoading(false);
      return () => {
        mounted = false;
      };
    }
  }, []);

  const signInAnonymously = async () => {
    try {
      const { data, error } = await supabase.auth.signInAnonymously();

      if (error) {
        // If anonymous sign-ins are disabled, just continue without auth
        if (error.message?.includes('Anonymous sign-ins are disabled')) {
          logger.warn('Anonymous sign-ins are disabled, continuing without auth', null, 'Auth');
          return;
        }
        logger.error('Error signing in anonymously', error, 'Auth');
        throw error;
      }

      logger.debug('Anonymous user signed in successfully', { userId: data.user?.id }, 'Auth');
      return;
    } catch (error) {
      logger.error('Error in signInAnonymously', error, 'Auth');
      throw error;
    }
  };

  const convertAnonymousToRegistered = async (email: string, password: string) => {
    try {
      if (!user || !user.is_anonymous) {
        return { success: false, error: 'User is not anonymous or not signed in' };
      }

      // Convert anonymous user to registered user
      const { data, error } = await supabase.auth.updateUser({
        email,
        password,
      });

      if (error) {
        logger.error('Error converting anonymous user', error, 'Auth');
        return { success: false, error: error.message };
      }

      // Create profile for the newly registered user using Real Profile Service
      if (data.user) {
        try {
          // Use Real Profile Service to create profile in AWS RDS
          const { realProfileService } = await import('@/services/RealProfileService');

          const profileData = {
            id: data.user.id,
            full_name: data.user.user_metadata?.full_name || email.split('@')[0],
            email: email,
            referral_code: 'REF' + Math.random().toString(36).substr(2, 8).toUpperCase(),
          };

          await realProfileService.updateProfile(data.user.id, profileData);
          logger.debug('Profile created successfully using Real Profile Service', { userId: data.user.id }, 'Auth');

          // TODO: Implement user role creation via Lambda Data Service
          // For now, user role will be default 'user' when fetched by getUserRoles()
          logger.debug('User role will default to "user" via Real Profile Service', { userId: data.user.id }, 'Auth');
        } catch (profileError) {
          logger.error('Error creating profile after conversion using Real Profile Service', profileError, 'Auth');
          // Don't fail the whole operation, profile can be created later
        }
      }

      logger.debug('Anonymous user converted to registered', { userId: data.user?.id }, 'Auth');
      return { success: true };
    } catch (error) {
      logger.error('Error in convertAnonymousToRegistered', error, 'Auth');
      return { success: false, error: 'Failed to convert anonymous user' };
    }
  };

  const signOut = async () => {
    try {
      // Sign out all users (including anonymous)
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear auth state
      setSession(null);
      setUser(null);

      // Clear stored data (but keep some for UX)
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('sb-htycplcuyoqfukhrughf-auth-token');

      logger.debug('User signed out successfully', null, 'Auth');
    } catch (error) {
      logger.error('Error signing out', error, 'Auth');
      throw error;
    }
  };

  const value = {
    session,
    user,
    isAnonymous,
    signOut,
    signInAnonymously,
    convertAnonymousToRegistered,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);

  // Dev mode bypass check (for customer authentication only)
  const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';
  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === 'true';

  if (isDevMode && bypassAuth && context === undefined) {
    // Return mock dev context when no provider is found but dev mode is enabled
    // Note: This is for customer authentication only. Admin authentication uses separate system.
    return {
      session: {
        user: {
          id: 'dev-customer-123',
          email: 'customer@iwishbag.com',
          user_metadata: { name: 'Dev Customer' }
        },
        access_token: 'dev-token'
      },
      user: {
        id: 'dev-customer-123',
        email: 'customer@iwishbag.com',
        user_metadata: { name: 'Dev Customer' },
        is_anonymous: false
      },
      isAnonymous: false,
      signOut: async () => {
        console.log('🚨 DEV MODE: Mock customer sign out');
      },
      signInAnonymously: async () => {
        console.log('🚨 DEV MODE: Customer already signed in');
      },
      convertAnonymousToRegistered: async () => {
        console.log('🚨 DEV MODE: Mock convert anonymous customer');
        return { success: true };
      }
    };
  }

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
